import { PDFDocument } from 'pdf-lib';

const DOC_ORDER = [
  { key: 'aadhar', label: 'Aadhar' },
  { key: 'pan', label: 'PAN' },
  { key: 'bank', label: 'Bank / IFSC proof' }
];

const dataUrlToBytes = (dataUrl) => {
  const raw = String(dataUrl || '');
  const base64 = raw.includes(',') ? raw.split(',')[1] : raw;
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const imageToPngBytes = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const pngUrl = canvas.toDataURL('image/png');
        resolve(dataUrlToBytes(pngUrl));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error('Unable to load image for PDF merge'));
    img.src = dataUrl;
  });

const addImagePage = async (pdfDoc, image) => {
  // One uploaded image = one page (fit to A4, keep aspect ratio)
  const pageW = 595;
  const pageH = 842;
  const margin = 24;
  const maxW = pageW - margin * 2;
  const maxH = pageH - margin * 2;
  const scale = Math.min(maxW / image.width, maxH / image.height, 1);
  const w = image.width * scale;
  const h = image.height * scale;
  const page = pdfDoc.addPage([pageW, pageH]);
  page.drawImage(image, {
    x: (pageW - w) / 2,
    y: (pageH - h) / 2,
    width: w,
    height: h
  });
};

/**
 * Merge Aadhar / PAN / Bank KYC files into a single PDF (documents only — no cover/text pages).
 */
export async function mergeKycDocumentsToPdf(member) {
  const docs = member?.kyc_documents && typeof member.kyc_documents === 'object'
    ? { ...member.kyc_documents }
    : {};

  // Legacy ifsc-only file
  if (!docs.bank?.data && docs.ifsc?.data) {
    docs.bank = docs.ifsc;
  }

  const present = DOC_ORDER.filter((d) => docs[d.key]?.data);
  if (!present.length) {
    throw new Error('No KYC documents to merge');
  }

  const pdfDoc = await PDFDocument.create();
  let added = 0;

  for (const { key, label } of present) {
    const doc = docs[key];
    const mime = String(doc.mime || '').toLowerCase();
    const isPdf = mime.includes('pdf') || String(doc.name || '').toLowerCase().endsWith('.pdf');

    if (isPdf) {
      try {
        const src = await PDFDocument.load(dataUrlToBytes(doc.data), { ignoreEncryption: true });
        const pages = await pdfDoc.copyPages(src, src.getPageIndices());
        pages.forEach((p) => pdfDoc.addPage(p));
        added += pages.length || 1;
      } catch (err) {
        console.error('PDF merge page failed', key, err);
        throw new Error(`Unable to include ${label} PDF in preview`);
      }
    } else {
      try {
        let image;
        if (mime.includes('png')) {
          image = await pdfDoc.embedPng(dataUrlToBytes(doc.data));
        } else if (mime.includes('jpeg') || mime.includes('jpg')) {
          image = await pdfDoc.embedJpg(dataUrlToBytes(doc.data));
        } else {
          const pngBytes = await imageToPngBytes(doc.data);
          image = await pdfDoc.embedPng(pngBytes);
        }
        await addImagePage(pdfDoc, image);
        added += 1;
      } catch (err) {
        console.error('Image merge page failed', key, err);
        throw new Error(`Unable to include ${label} image in preview`);
      }
    }
  }

  if (!added) {
    throw new Error('No KYC documents could be embedded');
  }

  return pdfDoc.save();
}

export function uint8ToPdfObjectUrl(bytes) {
  const copy = bytes instanceof Uint8Array
    ? bytes.slice()
    : new Uint8Array(bytes);
  const blob = new Blob([copy], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}
